import React, { useCallback, useEffect, useState } from 'react';
import { Button, Spinner, Table } from 'react-bootstrap';
import { apiGet, apiPost } from '../../services/api';
import AdminJobDetailModal from './AdminJobDetailModal';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function AdminPendingJobsTab({ token, setError, handleAuthError }) {
  const [pendingJobs, setPendingJobs] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [jobModalInitialReject, setJobModalInitialReject] = useState(false);

  const loadPendingJobs = useCallback(async () => {
    if (!token) return;
    setPendingLoading(true);
    setError('');
    try {
      const res = await apiGet('/api/v1/admin/jobs/pending', token);
      if (handleAuthError(res)) return;
      if (!res.ok) {
        setError('Could not load pending jobs.');
        return;
      }
      const json = await res.json();
      setPendingJobs(json.data ?? []);
    } catch {
      setError('Could not load pending jobs.');
    } finally {
      setPendingLoading(false);
    }
  }, [token, handleAuthError, setError]);

  useEffect(() => {
    loadPendingJobs();
  }, [loadPendingJobs]);

  const closeJobModal = () => {
    setShowJobModal(false);
    setSelectedJob(null);
    setJobModalInitialReject(false);
  };

  const openJobModal = (job, initialReject = false) => {
    setSelectedJob(job);
    setJobModalInitialReject(initialReject);
    setShowJobModal(true);
  };

  const handleJobModalApproved = () => {
    closeJobModal();
    loadPendingJobs();
  };

  const handleJobModalRejected = () => {
    closeJobModal();
    loadPendingJobs();
  };

  const approveJob = async (jobId) => {
    setActionLoadingId(jobId);
    setError('');
    try {
      const res = await apiPost(`/api/v1/admin/jobs/${jobId}/approve`, {}, token);
      if (handleAuthError(res)) return;
      if (!res.ok) {
        setError('Could not approve job.');
        return;
      }
      setPendingJobs((prev) => prev.filter((j) => j.id !== jobId));
    } catch {
      setError('Could not approve job.');
    } finally {
      setActionLoadingId(null);
    }
  };

  if (pendingLoading) {
    return <Spinner animation="border" size="sm" data-testid="rwm-admin-pending-loading" />;
  }

  if (pendingJobs.length === 0) {
    return (
      <p className="text-muted mb-0" data-testid="rwm-admin-pending-empty">
        No pending jobs.
      </p>
    );
  }

  return (
    <>
      <div className="table-responsive">
        <Table className="rwm-admin__table align-middle mb-0" data-testid="rwm-admin-pending-table">
          <thead>
            <tr>
              <th>Job title</th>
              <th>Company</th>
              <th>Submitted</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pendingJobs.map((job) => (
              <tr key={job.id} data-testid={`rwm-admin-pending-row-${job.id}`}>
                <td>
                  <button
                    type="button"
                    className="btn btn-link p-0 border-0 rwm-admin__job-title-link text-start"
                    onClick={() => openJobModal(job)}
                    data-testid={`rwm-admin-job-title-${job.id}`}
                  >
                    {job.title}
                  </button>
                  {job.rejection_count > 0 ? (
                    <div className="small text-warning mt-1">
                      Prior rejections: {job.rejection_count}
                      {job.reactivation_fee_required ? (
                        <span className="d-block fw-semibold">Reactivation fee applies</span>
                      ) : job.rejection_count >= 2 ? (
                        <span className="d-block">Next rejection triggers reactivation fee</span>
                      ) : null}
                    </div>
                  ) : null}
                </td>
                <td data-testid={`rwm-admin-pending-company-${job.id}`}>{job.company_name}</td>
                <td data-testid={`rwm-admin-pending-submitted-${job.id}`}>{formatDate(job.submitted_at)}</td>
                <td className="rwm-admin__actions">
                  <Button
                    size="sm"
                    variant="info"
                    className="me-2"
                    disabled={actionLoadingId === job.id}
                    onClick={() => approveJob(job.id)}
                    data-testid={`rwm-admin-job-approve-${job.id}`}
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={actionLoadingId === job.id}
                    onClick={() => openJobModal(job, true)}
                    data-testid={`rwm-admin-job-reject-${job.id}`}
                  >
                    Reject
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {showJobModal && selectedJob ? (
        <AdminJobDetailModal
          job={selectedJob}
          token={token}
          onClose={closeJobModal}
          onApproved={handleJobModalApproved}
          onRejected={handleJobModalRejected}
          initialShowReject={jobModalInitialReject}
          handleAuthError={handleAuthError}
          setError={setError}
        />
      ) : null}
    </>
  );
}
